// src/lib/managers/conversation/etapes.ts

/**
 * Contrat par étapes entre l'app et l'Api-Ai (docs/contrat-etapes.md de l'Api-Ai), activé par
 * l'option du SDK `conversationContract: "etapes"` (le web, en local). Un tour peut demander
 * plusieurs appels : l'Api-Ai demande une étape (lire l'agenda ou des mails), l'app l'exécute avec
 * son code actuel et rappelle avec le résultat, jusqu'à la fin du tour. Comme pour le contrat v1
 * (F-1.17), une réponse que cette version ne sait pas lire donne une réponse texte, jamais une
 * exception.
 */

import { parseGraphDateTimeToIsoString } from "../../utils/date/format-time";
import type { AgendaItem, AgendaTimeSlot } from "../agenda";
import { allDayCalendarDates, isBusyEvent } from "../agenda/freeSlots";
import type { Mail } from "../mail";
import { readFacts, type ConversationFact } from "./facts";
import { readQuestion, type EtapesQuestion } from "./question";
import type {
    AnswerModel,
    AssistantUsage,
    ConversationContextParts,
    ConversationContextSize,
    ReasoningItem,
} from "./types/conversation";

/**
 * Appels à l'Api-Ai par tour, au plus ; au-delà, le tour est raté (cause « service », F-1.17). L'Api-Ai
 * permet 3 allers-retours de lectures (`allers_retours_max`), puis la fin : 4. Un appel rejoué ne
 * compte pas.
 */
export const ETAPES_MAX_CALLS = 4;

/** Attente avant de rejouer un appel perdu (réseau, 502/503/504), une seule fois. */
export const ETAPES_REPLAY_DELAY_MS = 1_000;

/** Événements rendus par une lecture d'agenda, au plus ; au-delà, `tronque`. */
const LIRE_AGENDA_MAX_EVENTS = 200;

/** Mails rendus par une lecture de mails, au plus ; au-delà, `tronque`. */
export const LIRE_MAILS_MAX = 50;

/**
 * Mémoire d'une fin de tour (docs/refonte-assistant/CONCEPTION-memoire-conversation.md de l'Api-Ai) :
 * l'échange du tour, à garder tel quel, la place occupée par la conversation, et ses faits retenus. Lue sans confiance :
 * absents ou illisibles, ils sont `undefined` — le tour reste affiché, il ne sera pas dans la mémoire.
 */
export function readEtapesMemoire(response: unknown): {
    echange?: string;
    contexte?: ConversationContextSize;
    /** Les faits retenus après le tour ou le compactage, en entier (facts.ts) ; absents : `undefined`. */
    faits?: ConversationFact[];
} {
    if (!isRecord(response)) return {};
    const { echange, contexte } = response;
    const lu: {
        echange?: string;
        contexte?: ConversationContextSize;
        faits?: ConversationFact[];
    } = {};
    if (typeof echange === "string" && echange) lu.echange = echange;
    const faits = readFacts(response.faits);
    if (faits) lu.faits = faits;
    if (
        isRecord(contexte) &&
        typeof contexte.utilise === "number" &&
        typeof contexte.max === "number" &&
        contexte.utilise >= 0 &&
        contexte.max > 0
    ) {
        const parts = readContextParts(contexte.parts, contexte.utilise);
        lu.contexte = {
            used: contexte.utilise,
            max: contexte.max,
            ...(parts ? { parts } : {}),
        };
    }
    return lu;
}

/**
 * La place de la conversation juste après un compactage, que l'Api-Ai ne mesure pas : les consignes
 * gardent leur part mesurée au dernier tour, le résumé prend sa taille réelle (`summaryTokens`, les
 * tokens écrits par le modèle), il n'y a plus ni lectures ni réflexion. Une estimation, comme le
 * découpage. Sans place connue avant, ni taille du résumé : inconnue.
 */
export function compactedContextSize(
    before: ConversationContextSize | undefined,
    summaryTokens: unknown,
): ConversationContextSize | undefined {
    if (!before || typeof summaryTokens !== "number" || summaryTokens < 0) {
        return undefined;
    }
    const instructions = before.parts?.instructions ?? 0;
    const exchanges = Math.round(summaryTokens);
    return {
        used: instructions + exchanges,
        max: before.max,
        parts: { instructions, exchanges, reads: 0, reasoning: 0 },
    };
}

/** Un compte de tokens : entier, positif ou nul. */
const isCount = (value: unknown): value is number =>
    typeof value === "number" && Number.isInteger(value) && value >= 0;

/**
 * La consommation rendue par l'Api-Backend (`GET ai/assistant-usage`) : le total de la conversation et
 * celui du mois. Réponse illisible, ou sans conversation : inconnue.
 */
export function readAssistantUsage(
    response: unknown,
): AssistantUsage | undefined {
    if (!isRecord(response)) return undefined;
    const { month, conversation } = response;
    if (!isRecord(month) || !isRecord(conversation)) return undefined;
    if (
        !isCount(month.totalTokens) ||
        !isCount(conversation.totalTokens) ||
        typeof month.start !== "string"
    ) {
        return undefined;
    }
    return {
        conversationTokens: conversation.totalTokens,
        monthTokens: month.totalTokens,
        ...(isCount(month.maxTokens) && month.maxTokens > 0
            ? { monthMaxTokens: month.maxTokens }
            : {}),
        monthStart: month.start,
    };
}

/** Le découpage estimé de la place occupée ; illisible, ou dont la somme ne vaut pas le total : aucun. */
function readContextParts(
    parts: unknown,
    used: number,
): ConversationContextParts | undefined {
    if (!isRecord(parts)) return undefined;
    const lu = {
        instructions: parts.consignes,
        exchanges: parts.echanges,
        reads: parts.lectures,
        reasoning: parts.reflexion,
    };
    const valeurs = Object.values(lu);
    if (
        !valeurs.every(
            (n): n is number =>
                typeof n === "number" && Number.isInteger(n) && n >= 0,
        ) ||
        valeurs.reduce((a, b) => a + b, 0) !== used
    ) {
        return undefined;
    }
    return lu as ConversationContextParts;
}

/** Étape demandée par l'Api-Ai : l'app l'exécute, puis rend son résultat avec le même `id`. */
export type Etape = { id: string; outil: string; arguments: unknown };

/** Un événement tel que l'étape `lireAgenda` le rend à l'Api-Ai. */
export type EtapeEvenement = {
    /** Identifiant de l'événement pour ce tour (« e1 », « e2 »…), cité par l'affichage final. */
    ref: string;
    titre: string;
    /** Instant ISO 8601 UTC ; journée entière : date du calendrier « AAAA-MM-JJ ». */
    debut: string;
    /** Comme `debut` ; journée entière : le lendemain du dernier jour (fin exclue). */
    fin: string;
    journee: boolean;
    /** Règle des créneaux libres (`isBusyEvent`, F-1.12). */
    occupe: boolean;
};

/**
 * Ce que MailVista a déjà calculé du contenu d'un mail (`aiDetails`) : son résumé court et les tâches
 * pas encore faites. C'est tout ce que l'assistant sait du contenu : il ne lit jamais le texte du mail.
 */
export type EtapeFiche = { resume: string; taches: string[] };

/** Un mail tel que l'étape `lireMails` le rend à l'Api-Ai : son enveloppe et sa fiche, jamais son texte. */
export type EtapeMail = {
    /** Identifiant du mail pour ce tour (« m1 », « m2 »…), cité par l'affichage final. */
    ref: string;
    /** « Nom <adresse> », ou l'adresse seule. */
    de: string;
    objet: string;
    /** Date d'envoi, ISO 8601 UTC ; illisible : `null`. */
    date: string | null;
    lu: boolean;
    pj: boolean;
    categories: string[];
    focus: boolean;
    traite: boolean;
    /** Pas encore analysé par MailVista : `null`. */
    fiche: EtapeFiche | null;
};

/**
 * Un mail envoyé par l'utilisateur, tel que l'étape `lireMails` le rend pour la boîte « envoyes » :
 * ses destinataires au lieu de son expéditeur, et ni statuts ni fiche (MailVista n'analyse pas les
 * mails envoyés).
 */
export type EtapeMailEnvoye = {
    ref: string;
    envoye: true;
    /** Destinataires (à, cc, cci) : « Nom <adresse> », ou l'adresse seule. */
    a: string[];
    objet: string;
    /** Date d'envoi, ISO 8601 UTC ; illisible : `null`. */
    date: string | null;
    pj: boolean;
    /** Ref du mail reçu, déjà lu pendant la conversation, auquel celui-ci répond (même fil, plus ancien). */
    reponseA?: string;
};

/**
 * Un nom demandé à `lireMails` (expéditeur ou destinataire), tel que les contacts l'ont compris : le
 * code décide avec ses seuils, l'Api-Ai le dit. `introuvable` : la lecture cherche le nom tel quel
 * dans les adresses ; `ambigu` : l'adresse retenue, et les autres candidats presque aussi pertinents ;
 * `indisponible` : ni le RAG ni le carnet n'ont pu chercher, rien n'est lu.
 */
export type EtapePersonne = {
    nom: string;
    resultat: "trouve" | "introuvable" | "ambigu" | "indisponible";
    retenu?: string;
    autres?: string[];
};

/** Candidats cités au plus pour un nom ambigu, en plus de l'adresse retenue. */
const AUTRES_CANDIDATS_MAX = 2;

/** Ce que les contacts ont fait d'un nom demandé (`resolveBestContact`). */
export function toEtapePersonne(
    nom: string,
    resolution: {
        emails: string[];
        candidates?: { email: string }[];
        ambiguous?: boolean;
        unavailable?: boolean;
    },
): EtapePersonne {
    if (resolution.unavailable) return { nom, resultat: "indisponible" };
    const [retenu] = resolution.emails;
    if (!retenu) return { nom, resultat: "introuvable" };
    if (!resolution.ambiguous) return { nom, resultat: "trouve", retenu };
    const autres = (resolution.candidates ?? [])
        .map((candidat) => candidat.email)
        .filter((email) => email !== retenu)
        .slice(0, AUTRES_CANDIDATS_MAX);
    return { nom, resultat: "ambigu", retenu, autres };
}

/**
 * Résultat d'une étape. Une lecture impossible est une erreur, jamais une liste vide : une panne ne
 * vaut jamais « tout est libre » (F-1.11), ni « aucun mail ».
 */
export type EtapeResultat =
    | { evenements: EtapeEvenement[]; tronque: boolean }
    | {
          mails: (EtapeMail | EtapeMailEnvoye)[];
          total?: number;
          tronque: boolean;
          personnes?: EtapePersonne[];
          /** La boîte lue, quand ce n'est pas la boîte de réception. */
          boite?: "envoyes";
          /**
           * Conversation d'un projet qui a un périmètre : « projet », la lecture a été limitée à ses
           * adresses ; « hors », les adresses demandées en sortent toutes et rien n'a été lu.
           */
          perimetre?: "projet" | "hors";
      }
    // La lecture n'est pas faite : l'app a d'abord besoin d'une précision (deux contacts pour un nom…).
    | { question: EtapesQuestion }
    | { erreur: "agenda_illisible" | "boite_illisible" | "etape_inconnue" }
    // La lecture n'est pas faite : un nom demandé n'a pas pu être cherché dans les contacts.
    | { erreur: "contacts_indisponibles"; personnes: EtapePersonne[] };

/**
 * Un créneau cité par la réponse, calculé par l'Api-Ai (instants UTC), avec sa ref (« c1 »). `reply` :
 * le message à envoyer quand l'utilisateur clique le créneau (il le choisit, rien n'est créé au clic).
 */
export type EtapesCreneau = {
    ref: string;
    slot: AgendaTimeSlot;
    reply?: string;
};

/** Le message d'un créneau cliqué, au plus (la borne des réponses d'une question). */
const CRENEAU_REPLY_MAX = 300;

/** Une action proposée sur des mails lus pendant la conversation, que l'utilisateur confirme. */
export type EtapesAction = { action: string; refs: string[] };

/**
 * Un brouillon de mail, en texte brut : une réponse au mail lu `reponseA` (sa ref), ou un nouveau mail
 * d'objet `objet`. L'utilisateur l'envoie lui-même ; il n'a aucun destinataire.
 */
export type EtapesBrouillon = {
    texte: string;
    objet?: string;
    reponseA?: string;
};

/** Réponse de l'Api-Ai, telle que cette version la lit. */
export type EtapesReading =
    | { kind: "etapes"; etapes: Etape[]; etat: string }
    // Fin du tour : la réponse de l'assistant en texte, où il cite par leur ref ce qu'il montre ; les
    // créneaux cités ; au plus une proposition (une action sur des mails, un rendez-vous, appel v1, ou
    // un brouillon de mail).
    | {
          kind: "reponse";
          message: string;
          creneaux: EtapesCreneau[];
          action?: EtapesAction;
          rendezVous?: unknown;
          brouillon?: EtapesBrouillon;
          question?: EtapesQuestion;
      }
    // Fin en `toolCall` v1 : la réponse se lit comme une réponse v1 (`readToolCall`).
    | { kind: "toolCall" }
    // `reason` part dans les logs : nature du problème, jamais de contenu.
    | { kind: "illisible"; reason: string };

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/** Une chaîne que `Date.parse` sait lire. */
export const isDate = (value: unknown): value is string =>
    typeof value === "string" && !Number.isNaN(Date.parse(value));

/** Étapes demandées, chacune avec son `id` : sans lui, son résultat ne pourrait pas être rendu. */
const readEtapes = (etapes: unknown): Etape[] | null => {
    if (!Array.isArray(etapes) || etapes.length === 0) return null;
    const lues: Etape[] = [];
    for (const etape of etapes) {
        if (!isRecord(etape) || typeof etape.id !== "string" || !etape.id) {
            return null;
        }
        lues.push({
            id: etape.id,
            // Outil illisible : une étape inconnue, à laquelle l'app répond `etape_inconnue`.
            outil: typeof etape.outil === "string" ? etape.outil : "",
            arguments: etape.arguments,
        });
    }
    return lues;
};

/** Refs d'un affichage ; une ref qui n'est pas un texte est une ref inconnue : ignorée. */
const readRefs = (refs: unknown): string[] | null =>
    Array.isArray(refs)
        ? refs.filter((ref): ref is string => typeof ref === "string")
        : null;

/** Créneaux cités, tels quels (ni recalcul ni conversion) ; un créneau illisible est laissé de côté. */
const readCreneaux = (creneaux: unknown): EtapesCreneau[] =>
    Array.isArray(creneaux)
        ? creneaux.flatMap((creneau) =>
              isRecord(creneau) &&
              typeof creneau.ref === "string" &&
              isDate(creneau.debut) &&
              isDate(creneau.fin)
                  ? [
                        {
                            ref: creneau.ref,
                            slot: {
                                startDate: creneau.debut,
                                endDate: creneau.fin,
                            },
                            ...(typeof creneau.reponse === "string" &&
                            creneau.reponse.trim() &&
                            creneau.reponse.length <= CRENEAU_REPLY_MAX
                                ? { reply: creneau.reponse.trim() }
                                : {}),
                        },
                    ]
                  : [],
          )
        : [];

/** L'action proposée ; sans action ni ref lisibles : aucune. */
const readAction = (action: unknown): EtapesAction | undefined => {
    if (!isRecord(action) || typeof action.action !== "string")
        return undefined;
    const refs = readRefs(action.refs);
    return refs && refs.length > 0
        ? { action: action.action, refs }
        : undefined;
};

const nonVide = (value: unknown): value is string =>
    typeof value === "string" && value.trim().length > 0;

/** Le brouillon préparé ; sans texte lisible : aucun. */
const readBrouillon = (brouillon: unknown): EtapesBrouillon | undefined => {
    if (!isRecord(brouillon) || !nonVide(brouillon.texte)) return undefined;
    return {
        texte: brouillon.texte,
        ...(nonVide(brouillon.objet) ? { objet: brouillon.objet } : {}),
        ...(nonVide(brouillon.reponseA)
            ? { reponseA: brouillon.reponseA }
            : {}),
    };
};

/** Le modèle qui a écrit la réponse (champ facultatif `modeleUtilise`) ; illisible : aucun. */
export function readModeleUtilise(response: unknown): AnswerModel | undefined {
    if (!isRecord(response) || !isRecord(response.modeleUtilise))
        return undefined;
    const { nom, raisonnement } = response.modeleUtilise;
    return nonVide(nom)
        ? { name: nom, reasoning: raisonnement === true }
        : undefined;
}

const REASONING_KINDS: Record<string, ReasoningItem["kind"]> = {
    pensee: "thought",
    etape: "step",
};

/**
 * Raisonnement d'une réponse (champ facultatif `raisonnement`), dans l'ordre. Une ligne de type
 * inconnu ou sans texte est ignorée ; un champ illisible donne une liste vide, jamais une réponse
 * illisible.
 */
export function readRaisonnement(response: unknown): ReasoningItem[] {
    if (!isRecord(response) || !Array.isArray(response.raisonnement)) return [];
    const lignes: ReasoningItem[] = [];
    for (const ligne of response.raisonnement) {
        if (!isRecord(ligne) || typeof ligne.texte !== "string") continue;
        const kind =
            typeof ligne.type === "string"
                ? REASONING_KINDS[ligne.type]
                : undefined;
        if (kind && ligne.texte.trim())
            lignes.push({ kind, text: ligne.texte });
    }
    return lignes;
}

/**
 * Lit une réponse de l'Api-Ai au contrat par étapes, sans jamais lever : des étapes (avec l'état du
 * tour), ou la fin du tour — un `toolCall` v1, ou la réponse de l'assistant (texte, créneaux cités,
 * proposition). Une réponse vide est illisible ; une proposition illisible est laissée de côté.
 */
export function readEtapesResponse(response: unknown): EtapesReading {
    if (!isRecord(response)) {
        return { kind: "illisible", reason: "réponse qui n'est pas un objet" };
    }

    if (response.type === "etapes") {
        const etapes = readEtapes(response.etapes);
        if (typeof response.etat !== "string" || !response.etat || !etapes) {
            return {
                kind: "illisible",
                reason: "étapes sans état du tour, ou étape sans identifiant",
            };
        }
        return { kind: "etapes", etapes, etat: response.etat };
    }

    if (response.type !== "fin") {
        return { kind: "illisible", reason: "type de réponse inconnu" };
    }
    if (response.toolCall !== undefined && response.toolCall !== null) {
        return { kind: "toolCall" };
    }
    if (typeof response.message !== "string" || !response.message.trim()) {
        return { kind: "illisible", reason: "fin sans réponse" };
    }
    const action = readAction(response.action);
    const brouillon = readBrouillon(response.brouillon);
    const question = readQuestion(response.question);
    return {
        kind: "reponse",
        message: response.message,
        creneaux: readCreneaux(response.creneaux),
        ...(action ? { action } : {}),
        ...(isRecord(response.rendezVous)
            ? { rendezVous: response.rendezVous }
            : {}),
        ...(brouillon ? { brouillon } : {}),
        ...(question ? { question } : {}),
    };
}

/**
 * Un événement pour l'étape `lireAgenda`. Journée entière : dates du calendrier, lues comme pour les
 * créneaux libres. Dates illisibles : `null` — l'événement n'est pas rendu, comme les créneaux
 * libres l'ignorent.
 */
const toEtapeEvenement = (
    item: AgendaItem,
    ref: string,
): EtapeEvenement | null => {
    const periode = item.isAllDay
        ? allDayCalendarDates(item)
        : {
              start: parseGraphDateTimeToIsoString(item.start),
              end: parseGraphDateTimeToIsoString(item.end),
          };
    if (!periode?.start || !periode.end) {
        return null;
    }
    return {
        ref,
        titre: item.subject,
        debut: periode.start,
        fin: periode.end,
        journee: item.isAllDay,
        occupe: isBusyEvent(item),
    };
};

/**
 * Résultat de l'étape `lireAgenda` : les événements lus, dans l'ordre du fournisseur
 * (chronologique), `LIRE_AGENDA_MAX_EVENTS` au plus (`tronque` au-delà). `refOf` donne sa ref à
 * chaque événement rendu (et le garde pour l'affichage final) ; un événement aux dates illisibles
 * n'est pas rendu.
 */
export function buildLireAgendaResultat(
    items: AgendaItem[],
    refOf: (item: AgendaItem) => string,
): EtapeResultat {
    const evenements: EtapeEvenement[] = [];
    for (const item of items) {
        const lisible = toEtapeEvenement(item, "");
        if (!lisible) continue;
        if (evenements.length === LIRE_AGENDA_MAX_EVENTS) {
            return { evenements, tronque: true };
        }
        evenements.push({ ...lisible, ref: refOf(item) });
    }
    return { evenements, tronque: false };
}

/** La fiche d'un mail : ce que MailVista a calculé de son contenu ; sans analyse, `null`. */
const toEtapeFiche = (mail: Mail): EtapeFiche | null => {
    const details = mail.aiDetails;
    if (!details) return null;
    return {
        resume: details.mailSummary ?? "",
        taches: (details.toDo ?? [])
            .filter((tache) => !tache.done && tache.taskSummary)
            .map((tache) => tache.taskSummary),
    };
};

/** « Nom <adresse> », ou l'adresse seule. */
const toPersonLabel = (person?: { name?: string; email?: string }): string => {
    const { name = "", email = "" } = person ?? {};
    return name && name !== email ? `${name} <${email}>` : email;
};

/** Date d'envoi d'un mail, ISO 8601 UTC ; absente ou illisible : `null`. */
const toSentDate = (mail: Mail): string | null => {
    const sent =
        mail.sentDateTime === null || mail.sentDateTime === ""
            ? null
            : new Date(mail.sentDateTime);
    return sent && !Number.isNaN(sent.getTime()) ? sent.toISOString() : null;
};

/**
 * Un mail envoyé pour l'étape `lireMails` (boîte « envoyes ») : destinataires, objet, date, pièce
 * jointe, jamais son texte. `reponseA` : la ref du mail reçu auquel il répond, si la conversation
 * l'a déjà lu.
 */
export function toEtapeMailEnvoye(
    mail: Mail,
    ref: string,
    reponseA?: string,
): EtapeMailEnvoye {
    return {
        ref,
        envoye: true,
        a: [...(mail.to ?? []), ...(mail.cc ?? []), ...(mail.bcc ?? [])]
            .map(toPersonLabel)
            .filter((label) => label !== ""),
        objet: mail.subject,
        date: toSentDate(mail),
        pj: mail.hasAttachment,
        ...(reponseA ? { reponseA } : {}),
    };
}

/**
 * Parmi des mails reçus déjà lus, la ref de celui auquel le mail envoyé `sent` répond : même fil
 * (`conversationId`), envoyé avant lui ; s'il y en a plusieurs, le plus récent. Aucun : `undefined`.
 */
export function repliedRef(
    sent: Mail,
    received: Iterable<[string, Mail]>,
): string | undefined {
    const sentAt = Date.parse(toSentDate(sent) ?? "");
    if (!sent.conversationId || Number.isNaN(sentAt)) return undefined;
    let found: { ref: string; at: number } | undefined;
    for (const [ref, mail] of received) {
        if (mail.conversationId !== sent.conversationId) continue;
        const at = Date.parse(toSentDate(mail) ?? "");
        if (Number.isNaN(at) || at >= sentAt) continue;
        if (!found || at > found.at) found = { ref, at };
    }
    return found?.ref;
}

/** Un mail pour l'étape `lireMails` : expéditeur, objet, date, statuts et fiche, jamais son texte. */
export function toEtapeMail(mail: Mail, ref: string): EtapeMail {
    return {
        ref,
        de: toPersonLabel(mail.from),
        objet: mail.subject,
        date: toSentDate(mail),
        lu: mail.isRead,
        pj: mail.hasAttachment,
        categories: mail.aiDetails?.categories ?? [],
        focus: mail.isFocus,
        traite: mail.isUserProcessed,
        fiche: toEtapeFiche(mail),
    };
}

// #region Réponse de l'assistant

/** Une ref citée dans la réponse : un mail (« m3 »), un événement (« e2 »), un créneau (« c1 »). */
export const ANSWER_REF = /\[([mec]\d{1,4})\]/g;

/** Une ligne qui ne porte que des ref (une puce de liste permise) : elle montre ces éléments en entier. */
const REFS_ONLY_LINE = /^\s*(?:[-*•]\s*)?(?:\[[mec]\d{1,4}\][\s,;]*)+$/;

/**
 * La réponse découpée pour l'affichage : du Markdown (ses ref en ligne y restent, à remplacer par une
 * pastille), et les lignes qui ne portent que des ref, regroupées quand elles se suivent : l'app y
 * montre les éléments en entier (une liste de mails, d'événements, de créneaux).
 */
export type AnswerSegment =
    | { kind: "markdown"; text: string }
    | { kind: "refs"; refs: string[] };

export function splitAnswer(text: string): AnswerSegment[] {
    const segments: AnswerSegment[] = [];
    const lines: string[] = [];
    const flushMarkdown = () => {
        const markdown = lines.join("\n").trim();
        if (markdown) segments.push({ kind: "markdown", text: markdown });
        lines.length = 0;
    };
    for (const line of text.split("\n")) {
        if (!REFS_ONLY_LINE.test(line)) {
            lines.push(line);
            continue;
        }
        flushMarkdown();
        const refs = [...line.matchAll(ANSWER_REF)].map((match) => match[1]);
        const previous = segments[segments.length - 1];
        if (previous?.kind === "refs") previous.refs.push(...refs);
        else segments.push({ kind: "refs", refs });
    }
    flushMarkdown();
    return segments;
}

// #endregion Réponse de l'assistant
